import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useContext,
} from "react";
import {
  IconRobot,
  IconSend,
  IconMicrophone,
  IconMicrophoneOff,
  IconPlayerStop,
  IconBrain,
  IconMessageCircle,
  IconCopy,
  IconTrash,
  IconShare,
  IconBug,
  IconPhoto,
  IconX,
  IconFileText,
} from "@tabler/icons-react";
import { Form, Button, Toast, ToastContainer } from "react-bootstrap";
import UserContext from "../../../contexts/UserContext";
import useAgentTesting from "../../../services/AgentTestService";
import { PrepareAgentForTest } from "../../../services/AgentsServices";
import "./playground-mobile.css";
import Swal from "sweetalert2";
import PublicLinkModal from "../PublicLinkModal";
import { useNavigate } from "react-router-dom";
import ConversationDebugModal from "./components/ConversationDebugModal";
import MessageDebugModal from "./components/MessageDebugModal";
import AgentPromptModal from "./components/AgentPromptModal";

const Playground = ({ agentContext = null }) => {
  const { user } = useContext(UserContext);
  const { sendMessage, error: serviceError, setError } = useAgentTesting();
  const navigate = useNavigate();

  // Estados principais
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Estados de áudio
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [recordedAudio, setRecordedAudio] = useState(null);
  const [audioBlob, setAudioBlob] = useState(null);

  // Estados de imagem
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  // Estados de UI
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("info");
  const [showPublicLinkModal, setShowPublicLinkModal] = useState(false);

  // Estados de debug
  const [showConversationDebug, setShowConversationDebug] = useState(false);
  const [showMessageDebug, setShowMessageDebug] = useState(false);
  const [debugTargetMessage, setDebugTargetMessage] = useState(null);
  const [debugPreviousMessage, setDebugPreviousMessage] = useState(null);
  const [debugMessageIndex, setDebugMessageIndex] = useState(0);
  const [showAgentPrompt, setShowAgentPrompt] = useState(false);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const audioChunks = useRef([]);

  // ==========================================
  // FUNÇÕES DE NOTIFICAÇÃO
  // ==========================================

  const showNotification = (message, type = "info") => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  // ==========================================
  // SCROLL AUTOMÁTICO
  // ==========================================

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "end",
    });
  }, []);
  useEffect(() => {
    if (messages.length > 0) {
      setTimeout(scrollToBottom, 100);
    }
  }, [messages, scrollToBottom]);

  // Inicializar mensagem de boas-vindas e preparar agente quando agentContext estiver disponível
  useEffect(() => {
    const prepareAgent = async () => {
      if (agentContext && messages.length === 0) {
        try {
          // Preparar agente para teste (resetar prompt)
          await PrepareAgentForTest(agentContext.id);
        } catch (error) {
          console.warn("⚠️ Erro ao preparar agente para teste:", error);
        }

        const welcomeMessage = {
          id: Date.now(),
          text: `🤖 Agente "${agentContext.agent_name}" pronto para teste!`,
          isUser: false,
          timestamp: new Date().toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          }),
          isWelcome: true,
        };
        setMessages([welcomeMessage]);
        setError(null);
      }
    };

    prepareAgent();
  }, [agentContext, messages.length, setError]);

  // ==========================================
  // FUNCIONALIDADES DE ÁUDIO
  // ==========================================

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 16000,
        },
      });

      const recorder = new MediaRecorder(stream, {
        mimeType: "audio/webm;codecs=opus",
      });

      audioChunks.current = [];

      recorder.ondataavailable = (event) => {
        audioChunks.current.push(event.data);
      };

      recorder.onstop = () => {
        const audioBlob = new Blob(audioChunks.current, {
          type: "audio/webm;codecs=opus",
        });
        setAudioBlob(audioBlob);

        // Criar URL para preview
        const audioUrl = URL.createObjectURL(audioBlob);
        setRecordedAudio(audioUrl);

        // Parar todas as tracks do stream
        stream.getTracks().forEach((track) => track.stop());
      };

      recorder.start();
      setMediaRecorder(recorder);
      setIsRecording(true);
      showNotification("Gravação iniciada. Fale agora!", "info");
    } catch (error) {
      console.error("Erro ao iniciar gravação:", error);
      showNotification("Erro ao acessar microfone", "error");
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && mediaRecorder.state === "recording") {
      mediaRecorder.stop();
      setIsRecording(false);
      showNotification("Gravação finalizada!", "success");
    }
  };

  const sendAudioMessage = async () => {
    if (!audioBlob || !agentContext) return;

    try {
      setIsLoading(true);

      // Adicionar mensagem visual de áudio do usuário
      const audioMessage = {
        id: Date.now(),
        text: "🎤 Mensagem de áudio",
        audioUrl: recordedAudio,
        isUser: true,
        timestamp: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
        isAudio: true,
      };

      setMessages((prev) => [...prev, audioMessage]);

      // Preparar FormData para envio
      const formData = new FormData();
      formData.append("audio", audioBlob, "audio.webm");
      formData.append("id", agentContext.id);
      formData.append("phone", user.phone);
      formData.append("account_id", user.account_id);
      formData.append("agent_id", agentContext.id);
      formData.append("version_active", agentContext.version_active);
      formData.append("internal", true);
      formData.append("uid", user.phone);

      // Enviar áudio para a API usando o serviço de teste
      const response = await sendMessage(formData);
      if (response) {
        let responseText = "";
        if (response.status === 200 && response.data) {
          responseText = response.data;
        } else if (response.message) {
          responseText = response.message;
        } else {
          responseText = "";
        }

        // Não adicionar mensagem se o texto for null ou vazio
        if (responseText && responseText.trim()) {
          const aiResponse = {
            id: Date.now() + 1,
            text: responseText,
            isUser: false,
            timestamp: new Date().toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            }),
          };
          setMessages((prev) => [...prev, aiResponse]);
        }
      } else {
        throw new Error(serviceError || "Erro na comunicação com o agente");
      }

      // Limpar áudio gravado
      clearRecordedAudio();
    } catch (error) {
      console.error("Erro ao enviar áudio:", error);

      // Tratamento consistente com as mensagens de texto
      let errorText = "Erro ao enviar mensagem de áudio";
      let showNotificationError = false;

      if (error.response) {
        const { status, data } = error.response;
        const apiError = data?.error || data?.message || "Erro desconhecido";

        switch (status) {
          case 400:
            if (apiError === "API key não configurada") {
              errorText = `🔑 O agente "${agentContext.agent_name}" não possui API key configurada.\n\n💡 Para resolver:\n1. Acesse "Meus Agentes" no menu\n2. Edite o agente "${agentContext.agent_name}"\n3. Configure uma API key válida\n4. Salve as alterações`;
              showNotificationError = true;
              showNotification(
                "Configure a API key para enviar áudio",
                "error",
              );
            } else {
              errorText = `❌ Erro de validação: ${apiError}`;
            }
            break;
          case 401:
            errorText = "🔐 Acesso não autorizado. Verifique suas credenciais.";
            showNotificationError = true;
            break;
          case 403:
            errorText = "🚫 Você não tem permissão para acessar este agente.";
            showNotificationError = true;
            break;
          case 404:
            errorText = `❌ Agente "${agentContext.agent_name}" não foi encontrado.`;
            showNotificationError = true;
            break;
          case 500:
            errorText =
              "🔧 Erro interno do servidor. Tente novamente em alguns minutos.";
            showNotificationError = true;
            break;
          default:
            errorText = `❌ Erro ${status}: ${apiError}`;
        }
      } else if (error.message) {
        errorText = `❌ ${error.message}`;
      }

      const errorMessage = {
        id: Date.now() + 1,
        text: errorText,
        isUser: false,
        timestamp: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
        isError: true,
      };
      setMessages((prev) => [...prev, errorMessage]);

      // Mostrar notificação adicional para erros críticos
      if (showNotificationError) {
        setTimeout(() => {
          showNotification("Erro de comunicação com o agente", "error");
        }, 500);
      }
    } finally {
      setIsLoading(false);

      // Auto-focus após envio de áudio
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  };

  const clearRecordedAudio = () => {
    if (recordedAudio) {
      URL.revokeObjectURL(recordedAudio);
    }
    setRecordedAudio(null);
    setAudioBlob(null);
  };

  // ==========================================
  // FUNCIONALIDADES DE IMAGEM
  // ==========================================

  const handleImageSelect = (event) => {
    const file = event.target.files[0];

    if (!file) return;

    // Validar tipo de arquivo
    const allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/webp",
    ];
    if (!allowedTypes.includes(file.type)) {
      showNotification(
        "Formato não suportado. Use JPG, PNG, GIF ou WebP.",
        "error",
      );
      return;
    }

    // Validar tamanho (máximo 1MB)
    const maxSize = 1024 * 1024; // 1MB em bytes
    if (file.size > maxSize) {
      showNotification("Imagem muito grande. Máximo permitido: 1MB", "error");
      return;
    }

    // Criar preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target.result);
      setImageFile(file);
      setSelectedImage(file.name);
      showNotification(`Imagem "${file.name}" selecionada`, "success");
    };
    reader.readAsDataURL(file);

    // Limpar input para permitir reenvio do mesmo arquivo
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const clearSelectedImage = () => {
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
    }
    setImagePreview(null);
    setImageFile(null);
    setSelectedImage(null);
  };

  const sendImageMessage = async () => {
    if (!imageFile || !agentContext) return;

    try {
      setIsLoading(true);

      // Adicionar mensagem visual de imagem do usuário
      const imageMessage = {
        id: Date.now(),
        text: `📷 ${selectedImage}`,
        imageUrl: imagePreview,
        isUser: true,
        timestamp: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
        isImage: true,
      };

      setMessages((prev) => [...prev, imageMessage]);

      // Preparar FormData para envio
      const formData = new FormData();
      formData.append("image", imageFile, selectedImage);
      formData.append("id", agentContext.id);
      formData.append("phone", user.phone);
      formData.append("account_id", user.account_id);
      formData.append("agent_id", agentContext.id);
      formData.append("version_active", agentContext.version_active);
      formData.append("internal", true);
      formData.append("uid", user.phone);

      // Enviar imagem para a API usando o serviço de teste
      const response = await sendMessage(formData);
      console.log("Image response:", response);

      if (response) {
        let responseText = "";
        if (response.status === 200 && response.data) {
          responseText = response.data;
        } else if (response.message) {
          responseText = response.message;
        } else {
          responseText = "";
        }

        // Não adicionar mensagem se o texto for null ou vazio
        if (responseText && responseText.trim()) {
          const aiResponse = {
            id: Date.now() + 1,
            text: responseText,
            isUser: false,
            timestamp: new Date().toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            }),
          };
          setMessages((prev) => [...prev, aiResponse]);
        }
      } else {
        throw new Error(serviceError || "Erro na comunicação com o agente");
      }

      // Limpar imagem selecionada
      clearSelectedImage();
    } catch (error) {
      console.error("Erro ao enviar imagem:", error);

      // Tratamento de erro similar ao áudio
      let errorText = "Erro ao enviar imagem";
      let showNotificationError = false;

      if (error.response) {
        const { status, data } = error.response;
        const apiError = data?.error || data?.message || "Erro desconhecido";

        switch (status) {
          case 400:
            if (apiError === "API key não configurada") {
              errorText = `🔑 O agente "${agentContext.agent_name}" não possui API key configurada.\n\n💡 Para resolver:\n1. Acesse "Meus Agentes" no menu\n2. Edite o agente "${agentContext.agent_name}"\n3. Configure uma API key válida\n4. Salve as alterações`;
              showNotificationError = true;
              showNotification(
                "Configure a API key para enviar imagens",
                "error",
              );
            } else {
              errorText = `❌ Erro de validação: ${apiError}`;
            }
            break;
          case 413:
            errorText = "📷 Imagem muito grande. Máximo permitido: 1MB";
            showNotificationError = true;
            break;
          case 415:
            errorText =
              "📷 Formato de imagem não suportado. Use JPG, PNG, GIF ou WebP";
            showNotificationError = true;
            break;
          case 401:
            errorText = "🔐 Acesso não autorizado. Verifique suas credenciais.";
            showNotificationError = true;
            break;
          case 403:
            errorText = "🚫 Você não tem permissão para acessar este agente.";
            showNotificationError = true;
            break;
          case 404:
            errorText = `❌ Agente "${agentContext.agent_name}" não foi encontrado.`;
            showNotificationError = true;
            break;
          case 500:
            errorText =
              "🔧 Erro interno do servidor. Tente novamente em alguns minutos.";
            showNotificationError = true;
            break;
          default:
            errorText = `❌ Erro ${status}: ${apiError}`;
        }
      } else if (error.message) {
        errorText = `❌ ${error.message}`;
      }

      const errorMessage = {
        id: Date.now() + 1,
        text: errorText,
        isUser: false,
        timestamp: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
        isError: true,
      };
      setMessages((prev) => [...prev, errorMessage]);

      // Mostrar notificação adicional para erros críticos
      if (showNotificationError) {
        setTimeout(() => {
          showNotification("Erro de comunicação com o agente", "error");
        }, 500);
      }
    } finally {
      setIsLoading(false);

      // Auto-focus após envio de imagem
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  };

  // ==========================================
  // ENVIO DE MENSAGENS DE TEXTO
  // ==========================================

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading || !agentContext) return;

    const userMessage = {
      id: Date.now(),
      text: inputMessage,
      isUser: true,
      timestamp: new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      }),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputMessage("");
    setIsLoading(true);

    try {
      const messagePayload = {
        id: agentContext.id,
        phone: user.phone,
        message: inputMessage,
        account_id: user.account_id,
        agent_id: agentContext.id,
        version_active: agentContext.version_active,
        internal: true,
      };

      messagePayload.uid = user.phone;

      const response = await sendMessage(messagePayload);

      if (response) {
        let responseText = "";
        if (response.status === 200 && response.data) {
          responseText = response.data;
        } else if (response.message) {
          responseText = response.message;
        } else {
          responseText = "";
        }

        // Não adicionar mensagem se o texto for null ou vazio
        if (responseText && responseText.trim()) {
          const messageParts = processAllBreaks(responseText);

          if (messageParts.length === 1 && messageParts[0].type === "text") {
            const aiMessage = {
              id: Date.now() + 1,
              text: messageParts[0].content,
              isUser: false,
              timestamp: new Date().toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              }),
              messageType: "text",
            };
            setMessages((prev) => [...prev, aiMessage]);
          } else {
            await processMultiPartMessage(messageParts);
          }
        }

        // Garantir foco após processamento de mensagens múltiplas
        setTimeout(() => {
          inputRef.current?.focus();
        }, 200);
      } else {
        console.log(response);
        throw new Error(serviceError || "Erro na comunicação com o agente");
      }
    } catch (error) {
      console.error("Erro ao enviar mensagem:", error);

      // Tratamento específico para diferentes tipos de erro
      let errorText = "Falha na comunicação com o agente";
      let showNotificationError = false;

      // Verificar primeiro se é erro HTTP com response
      if (error.response) {
        const { status, data } = error.response;
        const apiError = data?.error || data?.message || "Erro desconhecido";

        switch (status) {
          case 400:
            if (apiError === "API key não configurada") {
              errorText = `🔑 O agente "${agentContext.agent_name}" não possui API key configurada.\n\n💡 Para resolver:\n1. Acesse "Meus Agentes" no menu\n2. Edite o agente "${agentContext.agent_name}"\n3. Configure uma API key válida\n4. Salve as alterações`;
              showNotificationError = true;
              showNotification(
                "Configure a API key para testar este agente",
                "error",
              );
            } else {
              errorText = `❌ Erro de validação: ${apiError}`;
            }
            break;
          case 401:
            errorText = "🔐 Acesso não autorizado. Verifique suas credenciais.";
            showNotificationError = true;
            break;
          case 403:
            errorText = "🚫 Você não tem permissão para acessar este agente.";
            showNotificationError = true;
            break;
          case 404:
            errorText = `❌ Agente "${agentContext.agent_name}" não foi encontrado.`;
            showNotificationError = true;
            break;
          case 500:
            errorText =
              "🔧 Erro interno do servidor. Tente novamente em alguns minutos.";
            showNotificationError = true;
            break;
          default:
            errorText = `❌ Erro ${status}: ${apiError}`;
        }
      } else if (error.message) {
        errorText = `❌ ${error.message}`;
      }

      const errorMessage = {
        id: Date.now() + 1,
        text: errorText,
        isUser: false,
        timestamp: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
        isError: true,
      };
      setMessages((prev) => [...prev, errorMessage]);

      // Mostrar notificação adicional para erros críticos
      if (showNotificationError) {
        setTimeout(() => {
          showNotification("Erro de comunicação com o agente", "error");
        }, 500);
      }
    } finally {
      setIsLoading(false);

      // Auto-focus no input após processamento completo
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // ==========================================
  // AÇÕES DA CONVERSA
  // ==========================================

  const clearConversation = async () => {
    // Exibir confirmação antes de limpar
    const result = await Swal.fire({
      icon: "warning",
      title: "Limpar conversa",
      text: "Esta ação irá deletar o histórico completo da conversa. Deseja continuar?",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Sim, limpar",
      cancelButtonText: "Cancelar",
      reverseButtons: true,
    });

    // Se o usuário cancelou, não fazer nada
    if (!result.isConfirmed) {
      return;
    }

    // Mostrar loader
    Swal.fire({
      title: "Limpando conversa...",
      text: "Aguarde enquanto processamos a solicitação.",
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    setMessages([]);
    setError(null);

    if (agentContext) {
      try {
        const phone = user.phone;
        const uid = user.phone;
        // Preparar agente para teste com limpeza de histórico
        await PrepareAgentForTest(agentContext.id, null, uid, 1, phone);

        // Fechar loader e mostrar sucesso
        Swal.fire({
          icon: "success",
          title: "Conversa resetada",
          text: "O contexto da conversa foi limpo com sucesso.",
          timer: 2500,
          showConfirmButton: false,
          toast: true,
          position: "bottom-end",
          timerProgressBar: true,
        });
      } catch (error) {
        console.warn("⚠️ Erro ao resetar conversa:", error);

        // Fechar loader e mostrar erro
        Swal.fire({
          icon: "error",
          title: "Erro ao limpar conversa",
          text: "Ocorreu um erro ao tentar limpar o histórico da conversa. Tente novamente.",
          confirmButtonText: "Fechar",
        });
      }

      // Re-adicionar mensagem de boas-vindas
      const welcomeMessage = {
        id: Date.now(),
        text: `🤖 Agente "${agentContext.agent_name}" pronto para teste!`,
        isUser: false,
        timestamp: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
        isWelcome: true,
      };
      setMessages([welcomeMessage]);
    }

    // Focar no input após limpar
    setTimeout(() => {
      inputRef.current?.focus();
    }, 300);
  };

  const copyConversation = () => {
    const conversationText = messages
      .filter((msg) => !msg.isWelcome)
      .map(
        (msg) =>
          `${msg.isUser ? "Você" : agentContext?.agent_name || "Agente"}: ${
            msg.text
          }`,
      )
      .join("\n\n");

    navigator.clipboard.writeText(conversationText);
    showNotification("Conversa copiada para área de transferência!", "success");
  };

  // Função para compartilhar agente
  const handleShareAgent = () => {
    if (!agentContext) return;
    setShowPublicLinkModal(true);
  };

  // ==========================================
  // PROCESSAMENTO DE MENSAGENS MÚLTIPLAS
  // ==========================================

  /**
   * Processa mensagens que contêm URLs de imagem e as divide em partes separadas
   */
  const processImageBreaks = (messageText) => {
    if (!messageText || typeof messageText !== "string")
      return [{ type: "text", content: messageText }];

    const imageUrlRegex =
      /(https?:\/\/\S+\.(?:jpg|jpeg|png|gif|webp|bmp|svg))/gi;
    const imageUrls = messageText.match(imageUrlRegex);

    if (!imageUrls || imageUrls.length === 0) {
      return [{ type: "text", content: messageText }];
    }

    const parts = [];
    let remainingText = messageText;

    imageUrls.forEach((imageUrl) => {
      const splitResult = remainingText.split(imageUrl);

      if (splitResult[0].trim()) {
        parts.push({
          type: "text",
          content: splitResult[0].trim(),
        });
      }

      parts.push({
        type: "image",
        content: imageUrl,
      });

      remainingText = splitResult.slice(1).join(imageUrl);
    });

    if (remainingText.trim()) {
      parts.push({
        type: "text",
        content: remainingText.trim(),
      });
    }

    return parts;
  };

  /**
   * Processa mensagens que contêm a tag [[break]] e as divide em partes separadas
   */
  const processMessageBreaks = (messageText) => {
    if (!messageText || typeof messageText !== "string") return [messageText];

    if (!messageText.includes("[[break]]")) {
      return [messageText];
    }

    return messageText
      .split("[[break]]")
      .map((part) => part.trim())
      .filter((part) => part.length > 0);
  };

  /**
   * Processa todas as quebras ([[break]] + imagens)
   */
  const processAllBreaks = (messageText) => {
    if (!messageText || typeof messageText !== "string")
      return [{ type: "text", content: messageText }];

    const breakParts = processMessageBreaks(messageText);
    const allParts = [];

    breakParts.forEach((part) => {
      const imageParts = processImageBreaks(part);
      allParts.push(...imageParts);
    });

    return allParts;
  };

  /**
   * Renderiza conteúdo de mensagem com suporte a imagens
   */
  const renderMessageContent = (text, messageType = "text") => {
    if (typeof text !== "string") return null;

    const cleanText = text.replace(/\[\[break\]\]/g, "").trim();

    if (messageType === "image") {
      return (
        <div className="playground-message-image-container">
          <img
            src={cleanText}
            alt="Imagem"
            className="playground-message-image"
            style={{
              maxWidth: "250px",
              maxHeight: "200px",
              objectFit: "cover",
            }}
            onError={(e) => {
              e.target.outerHTML = `
                <div class="playground-image-error">
                  <small>📷 Imagem não pôde ser carregada</small><br/>
                  <a href="${cleanText}" target="_blank" rel="noopener noreferrer" class="playground-image-link">
                    Ver imagem original
                  </a>
                </div>
              `;
            }}
          />
        </div>
      );
    }

    return (
      <span>
        {cleanText.split("\n").map((line, index) => (
          <span key={index} className="">
            {line}
            {index < cleanText.split("\n").length - 1 && <br />}
          </span>
        ))}
      </span>
    );
  };

  /**
   * Simula digitação com delay baseado no tamanho do texto
   */
  const getTypingDelay = (content, contentType = "text") => {
    const baseDelay = 800;

    if (contentType === "image") {
      return 1200;
    }

    const charDelay = content.length * 25;
    const maxDelay = 2500;

    return Math.min(baseDelay + charDelay, maxDelay);
  };

  /**
   * Promessa para simular delay
   */
  const simulateTyping = (duration) => {
    return new Promise((resolve) => setTimeout(resolve, duration));
  };

  /**
   * Processa mensagem com múltiplas partes (quebras e imagens)
   */
  const processMultiPartMessage = async (messageParts) => {
    for (let i = 0; i < messageParts.length; i++) {
      const part = messageParts[i];
      const content = part.content || part;
      const contentType = part.type || "text";

      if (i > 0) {
        const delay = getTypingDelay(content, contentType);
        await simulateTyping(delay);
      }

      const aiMessage = {
        id: Date.now() + i + 1 + Math.random() * 1000,
        text: content,
        isUser: false,
        timestamp: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
        isPartOf: messageParts.length > 1,
        partIndex: i + 1,
        totalParts: messageParts.length,
        messageType: contentType,
      };

      setMessages((prev) => [...prev, aiMessage]);
      setTimeout(() => scrollToBottom(), 100);
    }
  };

  // ==========================================
  // RENDERIZAÇÃO
  // ==========================================

  const renderMessage = (message) => {
    const getBubbleClasses = () => {
      let classes = "playground-message-container ";
      classes += message.isUser
        ? "playground-message-user"
        : "playground-message-bot";
      return classes;
    };

    const getContentClasses = () => {
      let classes = "playground-message-content ";
      if (message.isError) {
        classes += "playground-message-error";
      } else if (message.isWarning) {
        classes += "playground-message-warning";
      } else if (message.isUser) {
        classes += "playground-message-user-content";
      } else {
        classes += "playground-message-bot-content";
      }
      return classes;
    };

    const getMessageStyle = () => {
      const baseStyle = {
        maxWidth: "85%",
        wordWrap: "break-word",
        fontSize: "15px",
        lineHeight: "1.5",
        padding: "12px 16px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
      };

      if (message.isUser) {
        baseStyle.borderRadius = "20px 20px 6px 20px";
        baseStyle.background = "var(--gradient-primary)";
        baseStyle.marginLeft = "auto";
      } else {
        baseStyle.borderRadius = "20px 20px 20px 6px";
        baseStyle.background = "white";
        baseStyle.border = "1px solid #f1f5f9";
      }

      return baseStyle;
    };

    return (
      <div key={message.id} className={getBubbleClasses()}>
        <div className={getContentClasses()} style={getMessageStyle()}>
          {message.isAudio ? (
            <div className="playground-audio-message">
              <IconMicrophone size={16} />
              <span>Mensagem de áudio</span>
              {message.audioUrl && (
                <audio
                  controls
                  style={{ maxWidth: "200px", height: "30px" }}
                  className="playground-audio-player"
                >
                  <source src={message.audioUrl} type="audio/webm" />
                  Seu navegador não suporta áudio.
                </audio>
              )}
            </div>
          ) : message.isImage ? (
            <div className="playground-image-message">
              <IconPhoto size={16} />
              <span>{message.text}</span>
              {message.imageUrl && (
                <div className="playground-image-preview mt-2">
                  <img
                    src={message.imageUrl}
                    alt="Imagem enviada"
                    style={{
                      maxWidth: "200px",
                      maxHeight: "150px",
                      objectFit: "cover",
                      borderRadius: "8px",
                      border: "1px solid #e2e8f0",
                    }}
                  />
                </div>
              )}
            </div>
          ) : (
            renderMessageContent(message.text, message.messageType)
          )}

          <div
            className={`playground-message-timestamp ${
              message.isUser
                ? "playground-timestamp-user"
                : "playground-timestamp-bot"
            }`}
          >
            <span
              className={
                message.isUser || message.isError || message.isWarning
                  ? "playground-timestamp-light"
                  : "playground-timestamp-muted"
              }
            >
              {message.timestamp}
              {message.isPartOf && (
                <span className="playground-part-indicator">
                  <span className="playground-part-badge">
                    {message.partIndex}/{message.totalParts}
                    {message.messageType === "image" && " 🖼️"}
                  </span>
                </span>
              )}
              {!message.isUser && !message.isWelcome && (
                <button
                  onClick={() => {
                    const messageIndex = messages.findIndex(
                      (msg) => msg.id === message.id,
                    );
                    const previousMessage =
                      messageIndex > 0 ? messages[messageIndex - 1] : null;
                    setDebugTargetMessage(message);
                    setDebugPreviousMessage(previousMessage);
                    setDebugMessageIndex(messageIndex);
                    setShowMessageDebug(true);
                  }}
                  className="playground-message-debug-btn"
                  title="Analisar esta mensagem"
                  style={{
                    marginLeft: "8px",
                    padding: "2px 6px",
                    background: "var(--primary)",
                    border: "1px solid #e2e8f0",
                    borderRadius: "4px",
                    fontSize: "10px",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                  }}
                >
                  <IconBug size={12} color="#fff" />
                </button>
              )}
            </span>
          </div>
        </div>
      </div>
    );
  };

  // Render header conditionally based on mode
  const renderHeader = () => {
    return (
      <div className="playground-hub-header">
        <div className="playground-hub-header-content">
          <IconBrain className="playground-icon-primary" size={24} />
          <div>
            <h4 className="playground-hub-title">Playground</h4>
            {agentContext && (
              <p className="playground-hub-subtitle">
                Área de testes do agente{" "}
                <strong>{agentContext.agent_name}</strong>
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="playground-hub-wrapper">
      {renderHeader()}

      {!agentContext ? (
        <div className="playground-empty-state playground-empty-hub">
          <div className="playground-empty-icon">
            <IconMessageCircle size={36} />
          </div>
          <h5 className="playground-empty-title">Carregando agente...</h5>
          <p className="playground-empty-text">
            Aguarde enquanto carregamos as informações do agente.
          </p>
        </div>
      ) : !agentContext.is_active ? (
        <div className="playground-empty-state playground-empty-hub">
          <div
            className="playground-empty-icon"
            style={{ fontSize: "3rem", color: "#fbbf24" }}
          >
            ⚠️
          </div>
          <h5 className="playground-empty-title" style={{ color: "#d97706" }}>
            Agente Inativo
          </h5>
          <p className="playground-empty-text" style={{ marginBottom: "2rem" }}>
            O agente "<strong>{agentContext.agent_name}</strong>" precisa estar
            ativo para realizar testes no Playground.
            <br />
            Ative o agente na lista de agentes para começar a testar suas
            funcionalidades.
          </p>
          <Button
            variant="primary"
            size="lg"
            onClick={() => navigate("/agents")}
            className="d-flex align-items-center gap-2 mx-auto"
            style={{
              borderRadius: "12px",
              padding: "12px 24px",
              fontSize: "16px",
              fontWeight: 600,
              background: "var(--gradient-primary)",
              border: "none",
              boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3)",
            }}
          >
            <IconRobot size={20} />
            Gerenciar Agentes
          </Button>
        </div>
      ) : (
        <div className="playground-chat-container playground-chat-hub">
          {/* Agent Info Bar - Native Style */}
          <div
            className="playground-agent-info bg-white border-bottom"
            style={{ padding: "12px 16px" }}
          >
            <div className="playground-agent-info-content">
              <div className="playground-agent-info-left">
                {/* Avatar do Agente - Native Style */}
                <div className="playground-agent-avatar">
                  {agentContext.agent_image ? (
                    <img
                      src={agentContext.agent_image}
                      alt={agentContext.agent_name}
                      className="rounded-circle"
                      style={{
                        width: "44px",
                        height: "44px",
                        objectFit: "cover",
                        border: "2px solid #e2e8f0",
                      }}
                    />
                  ) : (
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center"
                      style={{
                        width: "44px",
                        height: "44px",
                        background: "var(--gradient-primary)",
                        color: "white",
                        border: "2px solid #e2e8f0",
                      }}
                    >
                      <IconRobot size={22} />
                    </div>
                  )}
                  <div className="status-indicator"></div>
                </div>

                {/* Info do Agente - Native Style */}
                <div className="playground-agent-details">
                  <div className="playground-agent-name-row">
                    <h6 className="playground-agent-name">
                      {agentContext.agent_name}
                    </h6>
                    <span className="playground-agent-status-badge">ATIVO</span>
                  </div>
                  <small className="playground-agent-job">
                    {agentContext.agent_job || "Assistente IA"}
                  </small>
                </div>
              </div>

              <div className="playground-header-buttons">
                <Button
                  variant="outline-success"
                  size="sm"
                  onClick={handleShareAgent}
                  title="Compartilhar agente"
                  className="playground-header-btn playground-share-btn"
                  style={{
                    width: "38px",
                    height: "38px",
                    padding: 0,
                    borderRadius: "12px",
                    border: "1.5px solid #22c55e",
                    background: "#f0fdf4",
                    color: "#22c55e",
                  }}
                >
                  <IconShare size={16} />
                </Button>

                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => setShowConversationDebug(true)}
                  disabled={messages.length <= 1}
                  title="Análise da conversa"
                  className="playground-header-btn playground-debug-btn"
                  style={{
                    width: "38px",
                    height: "38px",
                    padding: 0,
                    borderRadius: "12px",
                    border: "1.5px solid #3b82f6",
                    background: "#eff6ff",
                    color: "#3b82f6",
                  }}
                >
                  <IconBug size={16} />
                </Button>

                {/* <Button
                  variant="outline-info"
                  size="sm"
                  onClick={() => setShowAgentPrompt(true)}
                  title="Ver prompt do agente"
                  className="playground-header-btn playground-prompt-btn"
                  style={{
                    width: "38px",
                    height: "38px",
                    padding: 0,
                    borderRadius: "12px",
                    border: "1.5px solid #06b6d4",
                    background: "#f0fdff",
                    color: "#06b6d4",
                  }}
                >
                  <IconFileText size={16} />
                </Button> */}

                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={copyConversation}
                  disabled={messages.length <= 1}
                  title="Copiar conversa"
                  className="playground-header-btn playground-copy-btn"
                  style={{
                    width: "38px",
                    height: "38px",
                    padding: 0,
                    borderRadius: "12px",
                    border: "1.5px solid #e2e8f0",
                    background: "#f8fafc",
                  }}
                >
                  <IconCopy size={16} />
                </Button>

                <Button
                  variant="outline-warning"
                  size="sm"
                  onClick={clearConversation}
                  title="Limpar conversa"
                  className="playground-header-btn playground-clear-btn"
                  style={{
                    width: "38px",
                    height: "38px",
                    padding: 0,
                    borderRadius: "12px",
                    border: "1.5px solid #fbbf24",
                    background: "#fffbeb",
                    color: "#d97706",
                  }}
                >
                  <IconTrash size={16} />
                </Button>
              </div>
            </div>
          </div>

          {/* Chat Messages Area - Native Style */}
          <div
            className="flex-grow-1 playground-messages"
            style={{
              padding: "16px",
              height: "calc(100vh - 450px)",
              overflowY: "auto",
              scrollBehavior: "smooth",
              WebkitOverflowScrolling: "touch",
              background: "#f8fafc",
            }}
          >
            {messages.map(renderMessage)}

            {isLoading && (
              <div className="playground-typing-container">
                <div className="playground-typing">
                  <div className="playground-typing-header">
                    <small className="playground-typing-agent-name">
                      {agentContext.agent_name}
                    </small>
                  </div>
                  <div className="playground-typing-dots">
                    <div className="playground-typing-dot"></div>
                    <div className="playground-typing-dot"></div>
                    <div className="playground-typing-dot"></div>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input Area - Native Style */}
          <div
            className="bg-white border-top playground-input-container"
            style={{
              padding: "10px",
              borderTop: "1px solid #f1f5f9",
              background: "white",
              boxShadow: "0 -2px 12px rgba(0,0,0,0.04)",
            }}
          >
            {recordedAudio && (
              <div className="mb-3">
                <div
                  className="d-flex align-items-center gap-3 p-3 rounded-3 playground-audio-container"
                  style={{
                    background: "#eff6ff",
                    border: "1px solid #bfdbfe",
                    borderRadius: "16px",
                  }}
                >
                  <div className="playground-audio-icon">
                    <IconMicrophone size={16} />
                  </div>
                  <div className="playground-audio-content">
                    <small className="playground-audio-label">
                      Áudio gravado
                    </small>
                    <audio
                      controls
                      className="playground-audio-controls"
                      style={{ height: "32px", borderRadius: "8px" }}
                    >
                      <source src={recordedAudio} type="audio/webm" />
                    </audio>
                  </div>
                  <div className="playground-audio-buttons">
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={sendAudioMessage}
                      disabled={isLoading}
                      style={{
                        borderRadius: "10px",
                        padding: "6px 12px",
                        fontSize: "12px",
                        fontWeight: 600,
                      }}
                    >
                      Enviar
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={clearRecordedAudio}
                      style={{
                        borderRadius: "10px",
                        padding: "6px 8px",
                        fontSize: "12px",
                      }}
                    >
                      ✕
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {imagePreview && (
              <div className="mb-3">
                <div
                  className="d-flex align-items-center gap-3 p-3 rounded-3 playground-image-container"
                  style={{
                    background: "#f0fdf4",
                    border: "1px solid #bbf7d0",
                    borderRadius: "16px",
                  }}
                >
                  <div className="playground-image-icon">
                    <IconPhoto size={16} />
                  </div>
                  <div className="playground-image-content">
                    <small className="playground-image-label">
                      {selectedImage}
                    </small>
                    <div className="playground-image-preview-small mt-1">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        style={{
                          width: "60px",
                          height: "45px",
                          objectFit: "cover",
                          borderRadius: "6px",
                          border: "1px solid #d1d5db",
                        }}
                      />
                    </div>
                  </div>
                  <div className="playground-image-buttons">
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={sendImageMessage}
                      disabled={isLoading}
                      style={{
                        borderRadius: "10px",
                        padding: "6px 12px",
                        fontSize: "12px",
                        fontWeight: 600,
                      }}
                    >
                      Enviar
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={clearSelectedImage}
                      style={{
                        borderRadius: "10px",
                        padding: "6px 8px",
                        fontSize: "12px",
                      }}
                    >
                      ✕
                    </Button>
                  </div>
                </div>
              </div>
            )}

            <div className="playground-input-wrapper">
              <Form.Control
                as="textarea"
                ref={inputRef}
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyDown={handleKeyPress}
                placeholder={`Conversar com ${agentContext?.agent_name}...`}
                disabled={isLoading}
                rows={1}
                className="playground-textarea"
                style={{
                  resize: "none",
                  borderRadius: "18px",
                  border: "none",
                  background: "transparent",
                  padding: "12px 16px",
                  minHeight: "44px",
                  maxHeight: "120px",
                  fontSize: "15px",
                  lineHeight: "1.5",
                  boxShadow: "none",
                  outline: "none",
                }}
              />

              {/* Input file oculto */}
              <input
                type="file"
                ref={fileInputRef}
                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                onChange={handleImageSelect}
                style={{ display: "none" }}
              />

              {/* Botão de upload de imagem */}
              <Button
                variant="outline-primary"
                onClick={() => fileInputRef.current?.click()}
                disabled={isLoading || recordedAudio}
                title="Enviar imagem (máx. 1MB)"
                className="playground-action-btn playground-image-btn"
                style={{
                  width: "44px",
                  height: "44px",
                  padding: 0,
                  border: "none",
                  background: imagePreview ? "#10b981" : "var(--gray-200)",
                  color: imagePreview ? "white" : "var(--text-muted)",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }}
              >
                <IconPhoto size={20} />
              </Button>

              <Button
                variant={isRecording ? "danger" : "outline-primary"}
                onClick={isRecording ? stopRecording : startRecording}
                disabled={isLoading || imagePreview}
                title={isRecording ? "Parar gravação" : "Gravar áudio"}
                className={`playground-action-btn playground-mic-btn ${isRecording ? "playground-recording" : ""} ${recordedAudio ? "playground-has-audio" : ""}`}
                style={{
                  width: "44px",
                  height: "44px",
                  padding: 0,
                  border: "none",
                  background: isRecording
                    ? "var(--gradient-danger)"
                    : recordedAudio
                      ? "#f59e0b"
                      : "var(--gray-200)",
                  color:
                    isRecording || recordedAudio
                      ? "white"
                      : "var(--text-muted)",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }}
              >
                {isRecording ? (
                  <IconPlayerStop size={20} />
                ) : recordedAudio ? (
                  <IconMicrophoneOff size={20} />
                ) : (
                  <IconMicrophone size={20} />
                )}
              </Button>

              <Button
                onClick={handleSendMessage}
                disabled={!inputMessage.trim() || isLoading}
                title="Enviar mensagem"
                className={`playground-action-btn playground-send-btn ${inputMessage.trim() && !isLoading ? "playground-send-active" : ""}`}
                style={{
                  width: "44px",
                  height: "44px",
                  padding: 0,
                  background:
                    inputMessage.trim() && !isLoading
                      ? "var(--gradient-primary)"
                      : "var(--gray-300)",
                  border: "none",
                  color: "white",
                  boxShadow:
                    inputMessage.trim() && !isLoading
                      ? "0 2px 12px rgba(59, 130, 246, 0.3)"
                      : "none",
                  transform:
                    inputMessage.trim() && !isLoading
                      ? "scale(1.05)"
                      : "scale(1)",
                  transition: "all 0.2s ease",
                }}
              >
                <IconSend size={20} />
              </Button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer position="top-end" className="p-3">
        <Toast
          show={showToast}
          onClose={() => setShowToast(false)}
          bg={
            toastType === "error"
              ? "danger"
              : toastType === "warning"
                ? "warning"
                : "primary"
          }
          delay={3000}
          autohide
        >
          <Toast.Body className="text-white">{toastMessage}</Toast.Body>
        </Toast>
      </ToastContainer>

      {/* Modal de Gestão de Link Público */}
      <PublicLinkModal
        show={showPublicLinkModal}
        onHide={() => setShowPublicLinkModal(false)}
        agent={agentContext}
      />

      {/* Modais de Debug */}
      <ConversationDebugModal
        show={showConversationDebug}
        onHide={() => setShowConversationDebug(false)}
        messages={messages}
        agentContext={agentContext}
      />

      <MessageDebugModal
        show={showMessageDebug}
        onHide={() => setShowMessageDebug(false)}
        targetMessage={debugTargetMessage}
        previousMessage={debugPreviousMessage}
        messageIndex={debugMessageIndex}
        agentContext={agentContext}
      />

      <AgentPromptModal
        show={showAgentPrompt}
        onHide={() => setShowAgentPrompt(false)}
        agentContext={agentContext}
      />
    </div>
  );
};

export default Playground;
